import * as React from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Alert } from "@/components/ui/Alert";

interface AuthScreenProps {
  onLogin: (email: string, password: string, displayName?: string) => Promise<void>;
  error?: string;
  loading?: boolean;
}

export function AuthScreen({ onLogin, error, loading }: AuthScreenProps) {
  const [mode, setMode] = React.useState<"login" | "register">("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onLogin(email, password, mode === "register" ? displayName : undefined);
  }

  return (
    <main className="flex min-h-dvh items-start justify-center px-4 py-4 sm:py-8 lg:items-center lg:py-10">
      <div className="grid w-full max-w-5xl gap-4 sm:gap-6 lg:grid-cols-[1fr_420px] lg:items-center">
        <section className="flex flex-col gap-4 lg:gap-6">
          <div className="flex items-center gap-3 lg:block">
            <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-ink lg:size-12">
              <ShieldCheck className="size-5 lg:size-6" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-primary lg:mt-4 lg:hidden">RideShareTax</p>
          </div>
          <div className="flex max-w-2xl flex-col gap-2 lg:gap-4">
            <h1 className="text-balance text-2xl font-semibold tracking-tight text-ink sm:text-3xl lg:text-5xl">
              Prepare quarterly GST figures.
            </h1>
            <p className="hidden max-w-xl text-pretty text-base leading-7 text-muted sm:block">
              Enter rideshare income and expenses, then review BAS labels before entering them in ATO Online Services.
            </p>
          </div>
          <Alert title="BAS assistance only" className="hidden lg:block">
            RideShareTax does not lodge BAS and does not provide personal tax advice. Verify figures before lodging or ask a registered tax/BAS agent.
          </Alert>
        </section>

        <Card>
          <CardHeader>
            <CardTitle>{mode === "register" ? "Create workspace" : "Sign in"}</CardTitle>
            <CardDescription>Use your secure account to store GST worksheets, income, expenses, and source documents.</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-5" onSubmit={submit}>
              <FieldGroup>
                {mode === "register" ? (
                  <Field>
                    <FieldLabel htmlFor="displayName">Name</FieldLabel>
                    <Input
                      id="displayName"
                      name="name"
                      autoComplete="name"
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                    />
                  </Field>
                ) : null}
                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    name="username"
                    autoComplete="username"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <FieldDescription>Use at least 8 characters.</FieldDescription>
                </Field>
              </FieldGroup>

              {error ? <Alert variant="warning">{error}</Alert> : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button type="submit" disabled={loading} className="flex-1">
                  {loading ? "Checking..." : mode === "register" ? "Create account" : "Sign in"}
                </Button>
              </div>

              <button
                type="button"
                className="text-left text-sm font-medium text-primary underline-offset-4 hover:underline"
                onClick={() => setMode((current) => (current === "register" ? "login" : "register"))}
              >
                {mode === "register" ? "Already have an account? Sign in" : "Need an account? Create one"}
              </button>
            </form>
          </CardContent>
        </Card>

        <Alert title="BAS assistance only" className="lg:hidden">
          Review figures before lodging in ATO Online Services or with a registered tax/BAS agent.
        </Alert>
      </div>
    </main>
  );
}
